import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaHotel, FaUser, FaCheckCircle, FaExclamationCircle, FaImage } from "react-icons/fa";
import Swal from "sweetalert2";
import "./HotelOwners.css";

import { getFileUrl } from "../utils/imageUrl";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function GuideListingDetailsPage() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchDetails() {
      try {
        const res = await fetch(`${API}/admin/listings/guides/${tourId}`);
        const json = await res.json();
        if (res.ok) {
          setData(json);
        } else {
          setError(json.message || "Failed to load tour details");
        }
      } catch (e) {
        console.error(e);
        setError("Network error");
      } finally {
        setLoading(false);
      }
    }
    fetchDetails();
  }, [tourId]);

  const handleActivate = async () => {
    try {
      const result = await Swal.fire({
        title: "Activate/Unhold Tour?",
        text: "This will set the tour listing status to Active.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Activate",
        confirmButtonColor: "#10b981",
      });

      if (!result.isConfirmed) return;

      const res = await fetch(`${API}/admin/listings/guides/${tourId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Active" })
      });
      const resData = await res.json();

      if (res.ok) {
        Swal.fire("Activated!", "The tour listing status is now Active.", "success");
        setData(prev => prev ? { ...prev, statusName: "Active" } : null);
      } else {
        throw new Error(resData.message || "Failed to activate");
      }
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const handleIssue = async () => {
    try {
      const { value: formValues } = await Swal.fire({
        title: "Report an Issue",
        html: `
          <div style="text-align: left; margin-bottom: 10px;">
            <label style="display: block; font-size: 14px; font-weight: bold; margin-bottom: 8px;">Issue Description</label>
            <textarea id="swal-issue-details" class="swal2-textarea" style="width: 100%; box-sizing: border-box; height: 100px; margin: 0; font-size: 14px; padding: 10px;" placeholder="Type the issue details here..."></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Send Email & Delete",
        confirmButtonColor: "#ef4444",
        preConfirm: () => {
          const issueDetails = document.getElementById("swal-issue-details").value;
          if (!issueDetails.trim()) {
            Swal.showValidationMessage("Please enter issue details");
            return false;
          }
          return { issueDetails };
        }
      });

      if (!formValues) return;

      const res = await fetch(`${API}/admin/listings/guides/${tourId}/issue`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formValues),
      });
      const resData = await res.json();

      if (res.ok) {
        Swal.fire("Deleted!", "The tour listing has been deleted and the guide notified.", "success");
        navigate("/admin/listings");
      } else {
        throw new Error(resData.message || "Failed to report issue");
      }
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  if (loading) return <div className="owners-page"><div className="premium-glass-card">Loading...</div></div>;
  if (error) return <div className="owners-page"><div className="premium-glass-card" style={{ color: 'crimson' }}>{error}</div></div>;
  if (!data) return <div className="owners-page"><div className="premium-glass-card">Not found.</div></div>;

  const imageUrl = (path) => getFileUrl(path, API);

  return (
    <div className="owners-page">
      <div className="owners-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <button onClick={() => navigate(-1)} className="badge" style={{ marginBottom: '10px', background: '#fff', border: '1px solid #ddd', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <FaArrowLeft /> Back to List
          </button>
          <h2 className="owners-title">Tour Listing Details</h2>
          <p className="muted">Review information and photos for GL-{String(data.tourId).padStart(6, '0')}</p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {data.statusName !== "Active" && (
            <button onClick={handleActivate} className="badge" style={{ background: '#10b981', color: '#fff', fontSize: '14px', padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <FaCheckCircle /> Activate Tour
            </button>
          )}
          <button onClick={handleIssue} className="badge" style={{ background: '#ef4444', color: '#fff', fontSize: '14px', padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <FaExclamationCircle /> Report Issue & Delete
          </button>
        </div>
      </div>

      <div className="card table-card" style={{ padding: '30px', marginTop: '20px' }}>
        <div className="detail-section">
          <h4><FaHotel /> Listing & Tour Info</h4>
          <div className="detail-grid">
            <div className="detail-item"><label>Tour Title:</label> <span>{data.tourTitle || "—"}</span></div>
            <div className="detail-item"><label>Tour Type:</label> <span>{data.tourTypeName || "—"}</span></div>
            <div className="detail-item"><label>Service Area:</label> <span>{data.serviceArea || "—"}</span></div>
            <div className="detail-item"><label>Status:</label> <span className={`badge ${data.statusName === 'Active' ? 'green' : 'amber'}`}>{data.statusName || "Pending"}</span></div>
          </div>
          <div style={{ marginTop: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Description:</label>
            <p style={{ margin: 0, color: '#4b5563', lineHeight: '1.6' }}>{data.description || "No description provided."}</p>
          </div>
        </div>

        <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '30px 0' }} />

        <div className="detail-section">
          <h4><FaUser /> Guide Contact Details</h4>
          <div className="detail-grid">
            <div className="detail-item"><label>Guide Name:</label> <span>{data.guideName || "—"}</span></div>
            <div className="detail-item"><label>Languages Spoken:</label> <span>{data.languages || "—"}</span></div>
            <div className="detail-item"><label>Email Address:</label> <span>{data.email || "—"}</span></div>
            <div className="detail-item"><label>Phone Number:</label> <span>{data.phone || "—"}</span></div>
          </div>
        </div>

        {data.images && data.images.length > 0 && (
          <>
            <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '30px 0' }} />
            <div className="detail-section documents">
              <h4><FaImage /> Tour Gallery</h4>
              <div className="document-grid">
                {data.images.map((img, idx) => (
                  <div key={idx} className="doc-item">
                    <label>Photo {idx + 1}</label>
                    <div className="doc-preview" onClick={() => window.open(imageUrl(img), '_blank')} style={{ position: 'relative', cursor: 'pointer' }}>
                      <img
                        src={imageUrl(img)}
                        alt={`Tour ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
